/**
 * DrugsAtFdaPicker — the Drugs@FDA half of the US drug page ("Layout A").
 *
 * submission ▾ → `Label | Letter | Review ▾`. Each submission row comes from
 * `GET /drugs/{us_fda_id}/us_fda_submissions` and shows only the documents it
 * actually has. The Review sub-dropdown appears only when there are reviews;
 * a single review opens directly. Several linked applications render as one
 * optgroup each.
 *
 * Stateless: what is shown follows `selected` (the open document, from the
 * URL). Picking a submission opens its first document, so choosing in this
 * picker always makes Drugs@FDA the active source.
 */
import type { UsFdaProductRef, UsFdaSubmission } from '../types';

export type DrugsAtFdaSource = 'fdalabelpdfdoc' | 'approval-letter' | 'sba';

export interface DrugsAtFdaApp {
  product: UsFdaProductRef;
  submissions: UsFdaSubmission[];
}

interface Props {
  apps: DrugsAtFdaApp[];
  /** False → dimmed (the DailyMed picker is the active source). */
  active: boolean;
  /** The open Drugs@FDA document, or null when none is open. */
  selected: { source: DrugsAtFdaSource; id: number } | null;
  onOpen: (usFdaDrugId: number, source: DrugsAtFdaSource, documentId: number) => void;
}

export function submissionName(s: UsFdaSubmission): string {
  if (!s.submission_type) return 'Unassigned';
  return s.submission_number != null && s.submission_number !== ''
    ? `${s.submission_type}-${s.submission_number}`
    : s.submission_type;
}

function rowText(s: UsFdaSubmission): string {
  const docs = [
    s.label ? 'Label' : null,
    s.letter ? 'Letter' : null,
    s.reviews.length === 1 ? 'Review' : s.reviews.length > 1 ? `${s.reviews.length} Reviews` : null,
  ].filter(Boolean).join(', ');
  const date = s.submission_status_date ? ` (${s.submission_status_date.slice(0, 10)})` : '';
  return `${submissionName(s)}${date}${docs ? ` — ${docs}` : ''}`;
}

/** First document of a submission: label, else letter, else first review. */
export function firstDocument(
  s: UsFdaSubmission,
): { source: DrugsAtFdaSource; id: number } | null {
  if (s.label) return { source: 'fdalabelpdfdoc', id: s.label.document_id };
  if (s.letter) return { source: 'approval-letter', id: s.letter.document_id };
  if (s.reviews[0]) return { source: 'sba', id: s.reviews[0].document_id };
  return null;
}

function contains(s: UsFdaSubmission, sel: { source: DrugsAtFdaSource; id: number }): boolean {
  if (sel.source === 'fdalabelpdfdoc') return s.label?.document_id === sel.id;
  if (sel.source === 'approval-letter') return s.letter?.document_id === sel.id;
  return s.reviews.some((r) => r.document_id === sel.id);
}

const btn = (on: boolean) =>
  'rounded-md border px-2.5 py-1 text-sm transition-colors '
  + (on
    ? 'border-indigo-500 bg-indigo-600 text-white'
    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50');

export default function DrugsAtFdaPicker({ apps, active, selected, onOpen }: Props) {
  let key = '';
  if (selected) {
    apps.forEach((a, ai) => a.submissions.forEach((s, si) => {
      if (!key && contains(s, selected)) key = `${ai}:${si}`;
    }));
  }
  const [ai, si] = (key || '0:0').split(':').map(Number) as [number, number];
  const app = apps[ai];
  const sub = app?.submissions[si];
  if (!app || !sub) return null;

  const open = (source: DrugsAtFdaSource, id: number) => onOpen(app.product.drug_id, source, id);
  const isOpen = (source: DrugsAtFdaSource, id: number) =>
    !!selected && selected.source === source && selected.id === id;
  const options = (a: DrugsAtFdaApp, i: number) => a.submissions.map((s, j) => (
    <option key={`${i}:${j}`} value={`${i}:${j}`}>{rowText(s)}</option>
  ));

  return (
    <div
      data-testid="drugsatfda-picker"
      data-active={active ? 'true' : 'false'}
      className={`flex flex-wrap items-center gap-2 ${active ? '' : 'opacity-50'}`}
    >
      <span className="eyebrow">Drugs@FDA</span>
      <select
        aria-label="Drugs@FDA submission"
        value={key}
        onChange={(e) => {
          const [a, s] = e.target.value.split(':').map(Number) as [number, number];
          const target = apps[a]?.submissions[s];
          const doc = target && firstDocument(target);
          if (doc) onOpen(apps[a]!.product.drug_id, doc.source, doc.id);
        }}
        className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-800"
      >
        {!key && <option value="">Select submission…</option>}
        {apps.length > 1
          ? apps.map((a, i) => (
            <optgroup
              key={a.product.drug_id}
              label={[a.product.set_id, a.product.brand_name].filter(Boolean).join(' · ') || `Application ${i + 1}`}
            >
              {options(a, i)}
            </optgroup>
          ))
          : options(apps[0]!, 0)}
      </select>
      {sub.label && (
        <button type="button" aria-pressed={isOpen('fdalabelpdfdoc', sub.label.document_id)}
          className={btn(isOpen('fdalabelpdfdoc', sub.label.document_id))}
          onClick={() => open('fdalabelpdfdoc', sub.label!.document_id)}>
          Label
        </button>
      )}
      {sub.letter && (
        <button type="button" aria-pressed={isOpen('approval-letter', sub.letter.document_id)}
          className={btn(isOpen('approval-letter', sub.letter.document_id))}
          onClick={() => open('approval-letter', sub.letter!.document_id)}>
          Letter
        </button>
      )}
      {sub.reviews.length === 1 && (
        <button type="button" aria-pressed={isOpen('sba', sub.reviews[0]!.document_id)}
          className={btn(isOpen('sba', sub.reviews[0]!.document_id))}
          onClick={() => open('sba', sub.reviews[0]!.document_id)}>
          Review
        </button>
      )}
      {sub.reviews.length > 1 && (
        <select
          aria-label="Review"
          value={sub.reviews.find((r) => isOpen('sba', r.document_id))?.document_id ?? ''}
          onChange={(e) => { if (e.target.value) open('sba', Number(e.target.value)); }}
          className="rounded-md border border-gray-300 bg-white px-2 py-1 text-sm text-gray-800"
        >
          <option value="">Review ▾</option>
          {sub.reviews.map((r) => (
            <option key={r.document_id} value={r.document_id}>
              {r.title || r.doc_type || `Review ${r.document_id}`}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}
